import { Button, ButtonGroup } from "@datapay-by-datacom/design-system";
import {
  DocVersionStatus,
  SourceOrgCategory,
  VersionInfo,
} from "../../../generated/ingest-api";
import { useListVersions } from "../../hooks/OrgSource/useListVersions";
import { useApproveVersion } from "../../hooks/OrgSource/useApproveVersion";
import { useRejectVersion } from "../../hooks/OrgSource/useRejectVersion";
import styles from "./SourceVersionsPanel.module.css";

const LEGISLATION_BASE_URL = "https://legislation.govt.nz";

interface SourceVersionsPanelProps {
  category: SourceOrgCategory;
  orgId: string;
  docId: string;
  isAdmin: boolean;
}

const statusStyleMap: Record<DocVersionStatus, string> = {
  [DocVersionStatus.draft]: styles.statusDraft,
  [DocVersionStatus.pending_review]: styles.statusPendingReview,
  [DocVersionStatus.activating]: styles.statusActivating,
  [DocVersionStatus.active]: styles.statusActive,
  [DocVersionStatus.superseded]: styles.statusSuperseded,
  [DocVersionStatus.rejected]: styles.statusRejected,
  [DocVersionStatus.failed]: styles.statusFailed,
};

function formatStatus(status: DocVersionStatus): string {
  return status.replace(/_/g, " ");
}

function buildLegislationUrl(sourceVersion: string): string {
  return `${LEGISLATION_BASE_URL}/${sourceVersion.replace(/_/g, "/")}`;
}

function VersionActions({
  version,
  category,
  orgId,
  docId,
}: {
  version: VersionInfo;
  category: SourceOrgCategory;
  orgId: string;
  docId: string;
}) {
  const approveMutation = useApproveVersion(category, orgId, docId);
  const rejectMutation = useRejectVersion(category, orgId, docId);
  const isPending = approveMutation.isPending || rejectMutation.isPending;
  const isProcessing = approveMutation.isSuccess || rejectMutation.isSuccess;

  if (version.doc_version_status !== DocVersionStatus.pending_review) {
    return null;
  }

  if (isProcessing) {
    return (
      <span className={`${styles.statusBadge} ${styles.statusPendingReview}`}>
        processing
      </span>
    );
  }

  return (
    <ButtonGroup>
      <Button
        variant="primary"
        size="small"
        onPress={() => approveMutation.mutate()}
        isDisabled={isPending}
      >
        {approveMutation.isPending ? "Approving..." : "Approve"}
      </Button>
      <Button
        variant="secondary"
        size="small"
        onPress={() => rejectMutation.mutate()}
        isDisabled={isPending}
      >
        {rejectMutation.isPending ? "Rejecting..." : "Reject"}
      </Button>
    </ButtonGroup>
  );
}

export function SourceVersionsPanel({
  category,
  orgId,
  docId,
  isAdmin,
}: SourceVersionsPanelProps) {
  const { data: versions, isLoading, error } = useListVersions(
    category,
    orgId,
    docId,
    true
  );

  if (isLoading) {
    return <div className={styles.loadingVersions}>Loading versions...</div>;
  }

  if (error) {
    return <div className={styles.loadingVersions}>Failed to load versions</div>;
  }

  if (!versions || versions.length === 0) {
    return <div className={styles.emptyVersions}>No versions found</div>;
  }

  const showActions =
    isAdmin &&
    versions.some(
      (v) => v.doc_version_status === DocVersionStatus.pending_review
    );

  return (
    <div className={styles.panel}>
      <table className={styles.versionTable}>
        <thead>
          <tr>
            <th>Version</th>
            <th>Name</th>
            <th>Effective Date</th>
            <th>Status</th>
            {showActions && <th>Actions</th>}
          </tr>
        </thead>
        <tbody>
          {versions.map((version) => {
            const legislationUrl = version.source_version
              ? buildLegislationUrl(version.source_version)
              : null;

            return (
              <tr key={version.versioned_doc_id}>
                <td>
                  {legislationUrl ? (
                    <a
                      href={legislationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.versionLink}
                      title="Open this version"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {version.version}
                    </a>
                  ) : (
                    version.version
                  )}
                </td>
                <td>
                  {legislationUrl ? (
                    <a
                      href={legislationUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className={styles.versionLink}
                      title="Open this version"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {version.doc_name}
                    </a>
                  ) : (
                    version.doc_name
                  )}
                </td>
                <td>
                  {version.source_effective_date
                    ? new Date(version.source_effective_date).toLocaleDateString()
                    : "-"}
                </td>
                <td>
                  <span
                    className={`${styles.statusBadge} ${statusStyleMap[version.doc_version_status] ?? ""}`}
                  >
                    {formatStatus(version.doc_version_status)}
                  </span>
                </td>
                {showActions && (
                  <td className={styles.actionsCell}>
                    <VersionActions
                      version={version}
                      category={category}
                      orgId={orgId}
                      docId={docId}
                    />
                  </td>
                )}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
